import { UseQueryResult } from '@tanstack/react-query'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Button, Card, CardBody } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { usePartialUpdateBilancia } from '../../hooks/bilance'
import { Bilancia, InterventoDeadSimple } from '../../types'
import { useIsAmministratore, useIsCliente, useIsSupervisore } from '../../utils/utils'
import { CardTable } from '../../components/StickyTable'

interface CardRapportoTaraturaBilanciaInterface {
  id: Bilancia['id']
  rapporto_taratura: Bilancia['rapporto_taratura']
  periodo_controllo_qualita?: Bilancia['periodo_controllo_qualita']
  data_intervento_taratura?: Bilancia['data_intervento_taratura']
  data_scadenza_taratura?: Bilancia['data_scadenza_taratura']
  intervento_aperto_taratura?: InterventoDeadSimple
}

type CardRapportoTaraturaBilanciaProps = {
  bilancia: UseQueryResult<CardRapportoTaraturaBilanciaInterface, Error>
  modalActionsProgrammaIntervento?: {
    open: (v?: any) => void
    close: () => void
    toggle: () => void
    onClosed: () => void
  }
  modalActionsAddInterventoVeloce?: {
    open: (v?: any) => void
    close: () => void
    toggle: () => void
    onClosed: () => void
  }
  rapportoTaratura: boolean
}

export default function CardRapportoTaraturaBilancia({
  bilancia,
  modalActionsProgrammaIntervento,
  rapportoTaratura,
  modalActionsAddInterventoVeloce,
}: CardRapportoTaraturaBilanciaProps) {
  const { mutateAsync: updatePartialBilancia } = usePartialUpdateBilancia()
  const [periodicita, setPeriodicita] = useState<number | string>('')
  const isAdmin = useIsAmministratore()
  const isSupervisore = useIsSupervisore()
  const isCliente = useIsCliente()
  return (
    <Card className="mt-3 mb-3">
      <CardBody>
        <div className="py-1 border-bottom">
          <div className="d-flex justify-content-between">
            <h4 className="heading-h4">Rapporto taratura</h4>
          </div>
        </div>
        <div className="row mt-2">
          {bilancia.data?.rapporto_taratura && (
            <div className={`${isCliente ? 'col-md-12' : 'col-md-9'}`}>
              <div className="bg-rapporto-taratura p-2">
                <CardTable className="w-100" wrapperClassName="mobile-cards-flat">
                  <thead>
                    <tr className="text-rapporto-taratura">
                      <th className="p-lg">Cadenza</th>
                      <th className="p-lg">Ultimo</th>
                      <th className="p-lg">Scadenza</th>
                      <th className="p-lg">Program.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{bilancia.data?.periodo_controllo_qualita}</td>
                      <td>
                        {bilancia.data?.data_intervento_taratura
                          ? dayjs(bilancia.data?.data_intervento_taratura).format('DD.MM.YYYY')
                          : '-'}
                      </td>
                      <td>
                        {bilancia.data?.data_scadenza_taratura
                          ? dayjs(bilancia.data?.data_scadenza_taratura).format('DD.MM.YYYY')
                          : '-'}
                      </td>
                      <td>
                        {bilancia.data?.intervento_aperto_taratura?.stato && (
                          <Link to={`/interventi/${bilancia.data?.intervento_aperto_taratura?.pk}`} className="link-md">
                            {bilancia.data?.intervento_aperto_taratura?.data_intervento
                              ? dayjs(bilancia.data.intervento_aperto_taratura.data_intervento).format('DD/MM/YYYY')
                              : 'Da programmare'}
                          </Link>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </CardTable>
              </div>
            </div>
          )}
          <div
            className={classNames('col-md-3', {
              'col-md-12': !bilancia.data?.rapporto_taratura,
            })}
          >
            {rapportoTaratura ? (
              <>
                {!bilancia.data?.intervento_aperto_taratura?.stato && (isSupervisore || isAdmin) && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="w-100"
                    onClick={() => {
                      modalActionsProgrammaIntervento?.open({
                        bilancia: bilancia.data?.id,
                        tipo_intervento: 'rapporto_taratura',
                        stato: 'programmato',
                        data_intervento: dayjs().format('YYYY-MM-DD'),
                        note: '',
                      })
                    }}
                  >
                    Pianifica taratura
                  </Button>
                )}
                {!bilancia.data?.intervento_aperto_taratura?.stato && !isCliente && (
                  <Button
                    size="sm"
                    variant="secondary text-white"
                    className="w-100 mt-2"
                    onClick={() => {
                      modalActionsAddInterventoVeloce?.open({
                        bilancia: bilancia.data?.id,
                        tipo_intervento: 'rapporto_taratura',
                        data_intervento: dayjs().format('YYYY-MM-DD'),
                      })
                    }}
                  >
                    Intervento veloce
                  </Button>
                )}
              </>
            ) : (
              <div className="d-flex justify-content-center">
                <div>
                  <div>
                    Il servizio di rapporto taratura non è attivo per questa bilancia. Per abilitarlo, specificare la
                    periodicità della verifica
                  </div>
                  {!isCliente && (
                    <div className="d-flex justify-content-center flex-column w-100 mt-3">
                      <div className="d-flex justify-content-center flex-column align-items-center">
                        <input
                          type="number"
                          className="form-control"
                          value={periodicita}
                          placeholder="Cadenza"
                          style={{
                            width: '200px',
                          }}
                          onChange={(e) => setPeriodicita(parseInt(e.target.value))}
                        />
                        <Button
                          size="sm"
                          variant="secondary text-white"
                          className="mt-2"
                          style={{
                            width: '200px',
                          }}
                          disabled={!periodicita}
                          onClick={async () => {
                            await updatePartialBilancia({
                              rapporto_taratura: true,
                              periodo_controllo_qualita: periodicita as number,
                              id: bilancia.data?.id,
                            })
                          }}
                        >
                          Abilita taratura
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
